import type { Editor } from "@tiptap/vue-3";
import { onUnmounted, watch, ref } from "vue";
import type { Ref, ShallowRef, WatchHandle } from "vue";

const editor: ShallowRef<Editor | undefined> = ref();
const position = ref<{ x: number; y: number }>({ x: 0, y: 0 });

export type TextOptions = {
  fontSize?: number | null;
  fontSizeRelative?: number; // relative to scene font size, e.g. 1 = same as scene font size, 0.5 = half of scene font size
  fontFamily?: string | null;
  textAlign?: CanvasTextAlign | null;
  lineHeight?: number | null;
  textColor?: string | null;
}
const textOptions = ref<TextOptions>({});
const optionChangeListeners: Array<<K extends keyof TextOptions>(key: K, value: TextOptions[K]) => void> = [];

export const useNetTextToolBarContext = () => {
  const addedListeners = new Set(optionChangeListeners);
  let unwatchOptions: WatchHandle | null = null;
  function setPosition(value: { x: number; y: number }) {
    position.value = value;
  }
  function setEditor(value: Editor | undefined) {
    editor.value = value;
  }
  function setTextOptions(options: Ref<TextOptions>) {
    unwatchOptions = watch(options, (newOptions) => {
      textOptions.value = newOptions;
    }, { immediate: true });
  }
  function onUpdateOption(updateFunction: <K extends keyof TextOptions>(key: K, value: TextOptions[K]) => void) {
    optionChangeListeners.push(updateFunction);
    addedListeners.add(updateFunction);
  }
  function updateOption<K extends keyof TextOptions>(key: K, value: TextOptions[K]) {
    for (const updateFunction of optionChangeListeners) {
      updateFunction(key, value);
    }
  }
  function unbindListeners() {
    for (const listener of addedListeners) {
      const index = optionChangeListeners.indexOf(listener);
      if (index !== -1) {
        optionChangeListeners.splice(index, 1);
      }
    }
  }

  onUnmounted(() => {
    unbindListeners();
  });

  function disconnect() {
    unbindListeners();
    textOptions.value = {};
    editor.value = undefined;
    if (unwatchOptions) {
      unwatchOptions();
      unwatchOptions = null;
    }
  }

  return {
    editor,
    position,
    setEditor,
    setPosition,
    textOptions,
    setTextOptions,
    onUpdateOption,
    updateOption,
    disconnect
  };
};
